import { api, type ComponentsContext, type FittedComponent, type TaxonomyHit } from '../api';
import { callout, field, hoursVal, input, opt, openDialog, select, todayISO, val, numVal } from '../dialog';
import { hoursInputAttrs, hoursValue } from '../hours';
import { esc, fmtHours, html } from '../ui';

/**
 * Fitting and removing components.
 *
 * A component is not a part number: it carries accumulated life, a limit, and
 * the release document it was installed against. ML.A.501 does not permit
 * fitting one without that release, so the release is part of this form rather
 * than something to attach afterwards and forget.
 */

const CATEGORIES = [
  ['ENGINE', 'Engine'],
  ['PROPELLER', 'Propeller'],
  ['ROTABLE', 'Rotable'],
  ['LLP', 'Life-limited part'],
  ['EMERGENCY', 'Emergency equipment'],
  ['CONSUMABLE', 'Consumable'],
] as const;

const LIFE_KINDS = [
  ['ON_CONDITION', 'On condition — no fixed limit'],
  ['HARD_TIME', 'Hard time — overhaul at a limit'],
  ['LIFE_LIMITED', 'Life limited — scrapped at the limit'],
  ['SHELF_LIFE', 'Shelf life — expires by date'],
] as const;

const RELEASES = [
  ['EASA_FORM_1', 'EASA Form 1'],
  ['FAA_8130_3', 'FAA 8130-3'],
  ['DUAL_RELEASE', 'Dual release (Form 1 / 8130-3)'],
  ['CoC', 'Certificate of conformity'],
  ['SHELF_LIFE_CERT', 'Shelf-life certificate'],
] as const;

export function fitComponent(ctx: ComponentsContext, reload: () => void): void {
  let lifeKind = 'ON_CONDITION';
  // Held here rather than read off the select: the body is rebuilt on every
  // refresh, and a dropdown left to its own devices would snap back to Engine
  // the moment the life kind changed. `categoryChosen` is what stops the
  // taxonomy overwriting a deliberate choice with its own guess.
  let category = 'ENGINE';
  let categoryChosen = false;
  // What the register calls this part, kept apart from the description so that
  // editing the description afterwards cannot quietly take it out of step with
  // the register the AD search runs against.
  let easa: { holder: string; term: string } | null = null;

  openDialog<Record<string, unknown>>({
    wide: true,
    title: 'Fit component',
    sub: `${ctx.registration} — currently at ${ctx.tsnHours === null || ctx.tsnHours === undefined ? '—' : fmtHours(ctx.tsnHours)} h`,
    primary: 'Fit it',
    body: () => html`
      <div class="fset">
        <div class="lg">The part</div>
        ${field(
          'Find it in the EASA taxonomy',
          input('taxonomySearch', '', 'placeholder="Rotax 915, MTV-34, Lycoming IO-360…" data-taxonomy-search autocomplete="off"'),
          'Optional — fills the description with the term the AD register itself uses',
        )}
        <div class="pick" data-taxonomy hidden></div>
        <div class="frow3">
          ${field('Part number', input('partNumber', '', 'style="font-family:var(--mono)" maxlength="60"'))}
          ${field('Serial number', input('serialNumber', '', 'style="font-family:var(--mono)" maxlength="60"'))}
          ${field('ATA', input('ataChapter', '', 'placeholder="72" maxlength="8"'))}
        </div>
        ${field('Description', input('description', '', 'placeholder="Engine — Lycoming IO-360-L2A" maxlength="200"'))}
        <div class="frow3">
          ${field('Category', select('category', CATEGORIES.map(([v, l]) => opt(v, l, v === category)).join(''), 'data-category'))}
          ${field('Life', select('lifeKind', LIFE_KINDS.map(([v, l]) => opt(v, l, v === lifeKind)).join(''), 'data-life'))}
          ${field('Position', input('position', '', 'placeholder="ENG 1" style="font-family:var(--mono)" maxlength="24"'),
            ctx.positionsInUse.length ? `In use: ${ctx.positionsInUse.join(', ')}` : 'Nothing fitted yet')}
        </div>
      </div>

      ${lifeKind === 'SHELF_LIFE'
        ? field('Shelf life expires', input('shelfLifeExpiry', '', 'type="date"'))
        : lifeKind === 'ON_CONDITION'
          ? ''
          : html`
              <div class="fset">
                <div class="lg">Limit — whichever comes first</div>
                <div class="frow3">
                  ${field('Hours', input('limitHours', '', 'inputmode="decimal" style="text-align:right;font-family:var(--mono)"'))}
                  ${field('Cycles', input('limitCycles', '', 'inputmode="numeric" style="text-align:right;font-family:var(--mono)"'))}
                  ${field('Months', input('limitMonths', '', 'inputmode="numeric" style="text-align:right;font-family:var(--mono)"'))}
                </div>
                ${callout('caution', '!', html`
                  At least one limit is required. A hard-time or life-limited part without one would never
                  reach the due list, which is the failure this form exists to prevent.
                `)}
              </div>
            `}

      <div class="fset">
        <div class="lg">Life already accumulated</div>
        <div class="frow3">
          ${field('Hours since new', input('tsnHours', hoursValue(0), hoursInputAttrs()))}
          ${field('Hours since overhaul', input('tsoHours', hoursValue(0), hoursInputAttrs()))}
          ${field('Cycles since new', input('csn', '0', 'inputmode="numeric" style="text-align:right;font-family:var(--mono)"'))}
        </div>
        <div class="frow">
          ${field('Made on', input('manufacturedAt', '', 'type="date"'),
            'Off the Form 1. A life-limited part counts its months from here')}
          ${field('Last overhauled', input('lastOverhaulAt', '', 'type="date"'),
            'A hard-time part counts its months from here')}
        </div>
      </div>

      <div class="fset">
        <div class="lg">Release to service</div>
        <div class="frow3">
          ${field('Document', select('releaseKind', RELEASES.map(([v, l]) => opt(v, l)).join('')))}
          ${field('Form number', input('releaseFormNumber', '', 'style="font-family:var(--mono)" maxlength="60"'))}
          ${field('Issued', input('releaseIssuedAt', todayISO(), 'type="date"'))}
        </div>
        <div class="frow">
          ${field('Issuing organisation', input('releaseIssuingOrg', '', 'placeholder="RO.145.0031" maxlength="160"'))}
          ${field('Work scope', input('releaseWorkScope', '', 'placeholder="Overhauled / Inspected / New" maxlength="400"'))}
        </div>
        ${callout('info', 'i', html`
          ML.A.501 permits a component to be installed only when it is released on the appropriate
          document, so this is recorded with the fitment rather than attached afterwards.
        `)}
      </div>

      ${field('Fitted on', input('fittedAt', todayISO(), 'type="date"'))}
    `,
    onRender: (root, refresh) => {
      root.querySelector<HTMLSelectElement>('[data-life]')?.addEventListener('change', (e) => {
        lifeKind = (e.target as HTMLSelectElement).value;
        refresh();
      });
      root.querySelector<HTMLSelectElement>('[data-category]')?.addEventListener('change', (e) => {
        category = (e.target as HTMLSelectElement).value;
        categoryChosen = true;
      });
      bindTaxonomy(root, {
        picked: (hit) => { easa = { holder: hit.holder, term: hit.label }; },
        category: () => category,
        suggest: (c) => {
          if (categoryChosen) return;
          category = c;
          const el = root.querySelector<HTMLSelectElement>('[data-category]');
          if (el) el.value = c;
        },
      });
    },
    collect: (root) => {
      for (const [name, label] of [
        ['partNumber', 'part number'],
        ['description', 'description'],
        ['position', 'position'],
        ['releaseIssuingOrg', 'issuing organisation'],
      ] as const) {
        if (!val(root, name)) return { error: `The ${label} is required.` };
      }
      const kind = val(root, 'lifeKind');
      if ((kind === 'HARD_TIME' || kind === 'LIFE_LIMITED') &&
          !numVal(root, 'limitHours') && !numVal(root, 'limitCycles') && !numVal(root, 'limitMonths')) {
        return { error: 'Give at least one limit — hours, cycles or months.' };
      }
      if (kind === 'SHELF_LIFE' && !val(root, 'shelfLifeExpiry')) {
        return { error: 'A shelf-life component needs its expiry date.' };
      }
      // Months are the one limit with no counter behind them. Without a date to
      // count from, the only one available is the day it was fitted — which is
      // later than the day it was made, so the part would be reported due long
      // after it had actually expired.
      if (numVal(root, 'limitMonths') && !val(root, 'manufacturedAt') && !val(root, 'lastOverhaulAt')) {
        return {
          error:
            'A limit in months has to be counted from somewhere. Record when it was made, or when it was last overhauled.',
        };
      }

      return {
        partNumber: val(root, 'partNumber'),
        serialNumber: val(root, 'serialNumber') || undefined,
        description: val(root, 'description'),
        easaHolder: easa?.holder,
        easaTerm: easa?.term,
        ataChapter: val(root, 'ataChapter') || undefined,
        category: val(root, 'category'),
        lifeKind: kind,
        limitHours: numVal(root, 'limitHours'),
        limitCycles: numVal(root, 'limitCycles'),
        limitMonths: numVal(root, 'limitMonths'),
        shelfLifeExpiry: val(root, 'shelfLifeExpiry') || undefined,
        manufacturedAt: val(root, 'manufacturedAt') || undefined,
        lastOverhaulAt: val(root, 'lastOverhaulAt') || undefined,
        tsnHours: hoursVal(root, 'tsnHours'),
        tsoHours: hoursVal(root, 'tsoHours'),
        csn: numVal(root, 'csn'),
        position: val(root, 'position'),
        fittedAt: val(root, 'fittedAt'),
        releaseKind: val(root, 'releaseKind'),
        releaseFormNumber: val(root, 'releaseFormNumber') || undefined,
        releaseIssuingOrg: val(root, 'releaseIssuingOrg'),
        releaseIssuedAt: val(root, 'releaseIssuedAt'),
        releaseWorkScope: val(root, 'releaseWorkScope') || undefined,
      };
    },
    submit: (payload) => api.components.fit(ctx.aircraftId, payload),
    done: (r) => `${r.component.description} fitted at ${r.installation.position} on ${r.registration}`,
    onDone: reload,
  });
}

export function removeComponent(ctx: ComponentsContext, fitted: FittedComponent, reload: () => void): void {
  openDialog<Record<string, unknown>>({
    title: 'Remove component',
    sub: `${fitted.description} — ${fitted.partNumber}${fitted.serialNumber ? ` s/n ${fitted.serialNumber}` : ''}`,
    primary: 'Remove',
    body: () => html`
      <div class="frow">
        ${field('Removed on', input('removedAt', todayISO(), 'type="date"'))}
        ${field('Why', select('removalKind', [
          opt('SCHEDULED', 'Scheduled — reached its limit'),
          opt('UNSCHEDULED', 'Unscheduled — failed or suspect'),
          opt('MODIFICATION', 'Modification'),
          opt('ROBBED', 'Robbed for another aircraft'),
        ].join('')))}
      </div>
      ${field('Reason', input('removalReason', '', 'maxlength="400"'))}
      ${field('Condition on removal', select('newState', [
        opt('SERVICEABLE', 'Serviceable'),
        opt('UNSERVICEABLE', 'Unserviceable'),
        opt('IN_REPAIR', 'Sent for repair'),
        opt('SCRAPPED', 'Scrapped'),
      ].join('')))}
      ${callout('info', 'i', html`
        An unscheduled removal appears on the reliability report — that is how a pattern of early failures
        becomes visible rather than staying in individual records.
      `)}
    `,
    collect: (root) => {
      const removalReason = val(root, 'removalReason');
      if (!removalReason) return { error: 'Record why it came off.' };
      return {
        removedAt: val(root, 'removedAt'),
        removalKind: val(root, 'removalKind'),
        removalReason,
        newState: val(root, 'newState'),
      };
    },
    submit: (payload) => api.components.remove(ctx.aircraftId, fitted.installationId, payload),
    done: (r) => `${r.component} removed from ${r.position} — ${r.newState.toLowerCase()}`,
    onDone: reload,
  });
}

/**
 * The EASA type-certificate taxonomy, as a type-ahead on the description.
 *
 * Optional on purpose: it is a convenience over a free-text field, not a gate.
 * A component that is not on any European type certificate — an ELT, a vacuum
 * pump, anything under a standard change — still gets typed in, and the picker
 * simply finds nothing.
 *
 * Its value is the wording. We wrote "915 iSc 3A" for the engine on OK-AAA
 * where EASA writes "Rotax 915 iSc3 A", and "MTV-34-1-A" where EASA writes
 * "MTV-34-1". Those near-misses are what make a keyword search of the AD
 * register come back empty — which reads as "nothing has been published" rather
 * than "we asked the wrong question". Picking from here records the term the
 * register will actually be searched with.
 */
function bindTaxonomy(
  root: HTMLElement,
  category: {
    category: () => string;
    suggest: (c: 'ENGINE' | 'PROPELLER') => void;
    picked: (hit: TaxonomyHit) => void;
  },
): void {
  const search = root.querySelector<HTMLInputElement>('[data-taxonomy-search]');
  const panel = root.querySelector<HTMLElement>('[data-taxonomy]');
  if (!search || !panel) return;

  const get = (name: string) => root.querySelector<HTMLInputElement | HTMLSelectElement>(`[name="${name}"]`);

  let timer: number | undefined;
  let seq = 0;

  const pick = (hit: TaxonomyHit) => {
    category.picked(hit);
    // Only where the taxonomy is confident, and never over a choice already
    // made — a magneto turned up by an engine-maker search is still a rotable,
    // and the person fitting it knows that better than a name pattern does.
    if (hit.category) category.suggest(hit.category);

    const label = hit.category === 'ENGINE' ? 'Engine' : hit.category === 'PROPELLER' ? 'Propeller' : null;
    const description = get('description');
    if (description) description.value = label ? `${label} — ${hit.label}` : hit.label;

    panel.hidden = true;
    search.value = hit.label;
  };

  search.addEventListener('input', () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(async () => {
      const q = search.value.trim();
      if (q.length < 2) {
        panel.hidden = true;
        return;
      }
      const mine = ++seq;
      try {
        const r = await api.components.taxonomy(q, category.category());
        if (mine !== seq) return;

        panel.innerHTML = r.results.length
          ? r.results
              .map(
                (t, i) => html`<label data-hit="${i}">
                  <span class="pb">
                    <span class="pt">${esc(t.label)}</span>
                    <span class="pm">${esc(t.holder)}${t.model ? ` · type ${esc(t.type)}` : ''}</span>
                  </span>
                </label>`,
              )
              .join('')
          : html`<label><span class="pb"><span class="pt">Nothing on a European type certificate matches — type the description in below.</span></span></label>`;
        panel.hidden = false;

        panel.querySelectorAll<HTMLElement>('[data-hit]').forEach((el) =>
          el.addEventListener('click', () => pick(r.results[Number(el.dataset.hit)])),
        );
      } catch {
        if (mine === seq) panel.hidden = true;
      }
    }, 200);
  });
}

const FIELD_LABEL: Record<string, string> = {
  partNumber: 'part number',
  serialNumber: 'serial number',
  description: 'description',
  ataChapter: 'ATA chapter',
  position: 'position',
  easaHolder: 'certificate holder',
  easaTerm: 'EASA term',
  lifeKind: 'life kind',
  limitHours: 'hours limit',
  limitCycles: 'cycles limit',
  limitMonths: 'months limit',
  shelfLifeExpiry: 'shelf-life expiry',
  manufacturedAt: 'date of manufacture',
  lastOverhaulAt: 'date of last overhaul',
  tsnHours: 'hours since new',
  tsoHours: 'hours since overhaul',
  csn: 'cycles since new',
};

/**
 * Correcting a fitted component.
 *
 * Split the same way the aircraft record is, and for the same reason: an ATA
 * chapter and a position are descriptive, while the part number, the serial and
 * the life are what the forecast and every removal record are read against.
 * Change a limit and the part reaches the due list at a different moment,
 * silently — so those carry a reason, and the before and after go to the audit
 * trail.
 *
 * The release document is not here at all. ML.A.501 makes it the thing that
 * permitted the installation, so editing it afterwards would be rewriting the
 * authority under which the part went on the aircraft. A wrong release is
 * corrected by removing the component and fitting it again against the right
 * one, which leaves both facts on the record.
 */
export function editComponent(
  ctx: ComponentsContext,
  fitted: FittedComponent,
  reload: () => void,
): void {
  let correcting = false;
  let lifeKind = fitted.lifeKind;

  openDialog<Record<string, unknown>>({
    wide: true,
    title: `Edit ${fitted.description}`,
    sub: `${ctx.registration} · ${fitted.position} — ${fitted.partNumber}${fitted.serialNumber ? ` s/n ${fitted.serialNumber}` : ''}`,
    primary: 'Save',

    body: () => html`
      <div class="fset">
        <div class="lg">Details</div>
        ${field('Description', input('description', fitted.description, 'maxlength="200"'))}
        <div class="frow">
          ${field('ATA', input('ataChapter', fitted.ataChapter ?? '', 'placeholder="72" maxlength="8"'))}
          ${field('Position', input('position', fitted.position, 'style="font-family:var(--mono)" maxlength="24"'),
            ctx.positionsInUse.length ? `In use: ${ctx.positionsInUse.join(', ')}` : '')}
        </div>
        ${fitted.easaTerm
          ? callout('info', 'i', html`
              Recorded against <b>${esc(fitted.easaTerm)}</b>${fitted.easaHolder ? ` on the ${esc(fitted.easaHolder)} certificate` : ''}.
              That is what the directive check searches with, and an edit to the description above leaves
              it alone — which is the point of holding it separately.
            `)
          : ''}
      </div>

      <div class="fset">
        <div class="lg">Correct the record</div>
        <label style="display:flex;gap:9px;align-items:flex-start;font-size:13.5px">
          <input type="checkbox" data-correcting ${correcting ? 'checked' : ''} style="margin-top:3px">
          <span>I need to correct the <b>part number, serial number, limits or accumulated life</b>.</span>
        </label>

        ${correcting
          ? html`
              ${callout('caution', '!', html`
                The forecast and every removal on file are read against these. Changing a limit moves when
                the part becomes due, so the forecast is recomputed and the change is recorded with what it
                was, what it became and why.
              `)}

              <div class="frow">
                ${field('Part number', input('partNumber', fitted.partNumber, 'style="font-family:var(--mono)" maxlength="60"'))}
                ${field('Serial number', input('serialNumber', fitted.serialNumber ?? '', 'style="font-family:var(--mono)" maxlength="60"'))}
              </div>

              ${field('Life', select('lifeKind', LIFE_KINDS.map(([v, l]) => opt(v, l, v === lifeKind)).join(''), 'data-edit-life'))}

              ${lifeKind === 'SHELF_LIFE'
                ? field('Shelf life expires', input('shelfLifeExpiry', fitted.shelfLifeExpiry?.slice(0, 10) ?? '', 'type="date"'))
                : lifeKind === 'ON_CONDITION'
                  ? ''
                  : html`
                      <div class="frow3">
                        ${field('Hours', input('limitHours', fitted.limitHours ?? '', 'inputmode="decimal" style="text-align:right;font-family:var(--mono)"'))}
                        ${field('Cycles', input('limitCycles', fitted.limitCycles ?? '', 'inputmode="numeric" style="text-align:right;font-family:var(--mono)"'))}
                        ${field('Months', input('limitMonths', fitted.limitMonths ?? '', 'inputmode="numeric" style="text-align:right;font-family:var(--mono)"'))}
                      </div>
                    `}

              <div class="frow3">
                ${field('Hours since new', input('tsnHours', hoursValue(fitted.tsnHours ?? 0), hoursInputAttrs()))}
                ${field('Hours since overhaul', input('tsoHours', hoursValue(fitted.tsoHours ?? 0), hoursInputAttrs()))}
                ${field('Cycles since new', input('csn', fitted.csn ?? 0, 'inputmode="numeric" style="text-align:right;font-family:var(--mono)"'))}
              </div>

              <div class="frow">
                ${field('Made on', input('manufacturedAt', fitted.manufacturedAt?.slice(0, 10) ?? '', 'type="date"'),
                  'A life-limited part counts its months from here')}
                ${field('Last overhauled', input('lastOverhaulAt', fitted.lastOverhaulAt?.slice(0, 10) ?? '', 'type="date"'),
                  'A hard-time part counts its months from here')}
              </div>

              ${field('Reason for the correction',
                input('reason', '', 'placeholder="Serial transposed from the Form 1 at fitment; the plate reads …" maxlength="400"'),
                'Recorded with the before and after')}
            `
          : ''}
      </div>

      ${callout('info', 'i', html`
        The release document is not editable. ML.A.501 makes it the authority under which this part was
        installed, so a wrong one is corrected by removing the component and fitting it again against the
        right release — which leaves both facts on the record.
      `)}
    `,

    onRender: (root, refresh) => {
      root.querySelector<HTMLInputElement>('[data-correcting]')?.addEventListener('change', (e) => {
        correcting = (e.target as HTMLInputElement).checked;
        refresh();
      });
      root.querySelector<HTMLSelectElement>('[data-edit-life]')?.addEventListener('change', (e) => {
        lifeKind = (e.target as HTMLSelectElement).value;
        refresh();
      });
    },

    collect: (root) => {
      const payload: Record<string, unknown> = {
        description: val(root, 'description').trim() || undefined,
        ataChapter: val(root, 'ataChapter').trim() || undefined,
        position: val(root, 'position').trim() || undefined,
      };
      if (!payload.description) return { error: 'A description is required.' };
      if (!payload.position) return { error: 'A position is required.' };

      if (correcting) {
        const partNumber = val(root, 'partNumber').trim();
        if (!partNumber) return { error: 'A part number is required.' };
        if (numVal(root, 'limitMonths') && !val(root, 'manufacturedAt') && !val(root, 'lastOverhaulAt')) {
          return {
            error:
              'A limit in months has to be counted from somewhere. Record when it was made, or when it was last overhauled.',
          };
        }
        Object.assign(payload, {
          partNumber,
          serialNumber: val(root, 'serialNumber').trim() || undefined,
          lifeKind: val(root, 'lifeKind'),
          limitHours: numVal(root, 'limitHours'),
          limitCycles: numVal(root, 'limitCycles'),
          limitMonths: numVal(root, 'limitMonths'),
          shelfLifeExpiry: val(root, 'shelfLifeExpiry') || undefined,
          manufacturedAt: val(root, 'manufacturedAt') || undefined,
          lastOverhaulAt: val(root, 'lastOverhaulAt') || undefined,
          tsnHours: hoursVal(root, 'tsnHours'),
          tsoHours: hoursVal(root, 'tsoHours'),
          csn: numVal(root, 'csn'),
          reason: val(root, 'reason').trim() || undefined,
        });
      }
      return payload;
    },

    submit: (payload) => api.components.update(ctx.aircraftId, fitted.installationId, payload),
    done: (r) =>
      r.unchanged
        ? 'Nothing changed'
        : `${r.component} updated — ${r.changed.map((f: string) => FIELD_LABEL[f] ?? f).join(', ')}${
            r.forecastRecomputed ? ' · forecast recomputed' : ''
          }`,
    onDone: reload,
  });
}
